import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AlertTriangle, UserCheck, UserPlus } from 'lucide-react';
import { useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { SeparatorDot } from '~/components/Separated';
import { dicomDateToIso } from '~/domain/dicom/importScan';
import { PatientIdText, QuietButton } from '~/containers/patient-list/shared';
import {
    ageInYears,
    formatDayText,
    formatPatientName,
    sexAgeText,
} from '~/containers/patient-list/model';
import type { ImportIdentity, ImportIdentityField, Verdict } from '../model';
import PatientLabel from './PatientLabel';

const IDENTITY_FIELD_LABEL_ID: Record<ImportIdentityField, string> = {
    name: 'patientList.import.identity.field.name',
    sex: 'patientList.import.identity.field.sex',
    birthDate: 'patientList.import.identity.field.birthDate',
};

type ImportIdentityCardProps = {
    identity: ImportIdentity;
    /** Files this patient contributes to the drop; the meta line's trailing figure. */
    fileCount: number;
    /** Existence check for this patient; drives the New/Existing flag, omitted once resolved. */
    verdict?: Verdict;
    /** Only used when `verdict.kind === 'failed'`. */
    onRetryCheck?: () => void;
};

// The identity the dropped files declare; presentational, no input (the DICOM tag decides the patient).
export default function ImportIdentityCard({
    identity,
    fileCount,
    verdict,
    onRetryCheck,
}: ImportIdentityCardProps) {
    const intl = useIntl();
    const name = formatPatientName(identity.dicomPatientName);
    const birthDateIso = dicomDateToIso(identity.patientBirthDate);
    const age = ageInYears(birthDateIso, new Date());
    const birthDay = formatDayText(intl, birthDateIso);
    const sexAge = sexAgeText(intl, identity.patientSex, age);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.75,
                px: 2.5,
                borderBottom: 1,
                borderColor: 'divider',
                flexShrink: 0,
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    noWrap
                    sx={{
                        fontSize: name ? 16 : 14,
                        fontWeight: 500,
                        fontFamily: name ? undefined : 'fontFamilyMonospace',
                    }}
                >
                    <PatientLabel identity={identity} />
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.5,
                        minWidth: 0,
                        fontSize: 12,
                        color: 'text.secondary',
                    }}
                >
                    {name ? (
                        <>
                            {(sexAge || birthDay) && (
                                <Box
                                    component="span"
                                    sx={{
                                        fontFamily: 'fontFamilyMonospace',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {sexAge}
                                    {birthDay && (
                                        <>
                                            {sexAge ? ' ' : ''}(
                                            <SensitiveInfoTypography>
                                                {birthDay}
                                            </SensitiveInfoTypography>
                                            )
                                        </>
                                    )}
                                </Box>
                            )}
                            <SeparatorDot />
                            <PatientIdText id={identity.dicomPatientId} />
                        </>
                    ) : (
                        <Box
                            component="span"
                            sx={{
                                fontStyle: 'italic',
                                color: 'text.disabled',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {intl.formatMessage({
                                id: 'patientList.import.identity.noName',
                            })}
                        </Box>
                    )}
                    <SeparatorDot />
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                        {intl.formatMessage(
                            { id: 'patientList.import.identity.fileCount' },
                            { count: fileCount }
                        )}
                    </Box>
                </Box>
                {identity.conflicting.length > 0 && (
                    <Box
                        role="alert"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mt: 0.75,
                            fontSize: 12,
                            color: 'warning.main',
                        }}
                    >
                        <AlertTriangle size={13} />
                        {intl.formatMessage(
                            {
                                id: 'patientList.import.identity.conflicting',
                            },
                            {
                                fields: intl.formatList(
                                    identity.conflicting.map((field) =>
                                        intl.formatMessage({
                                            id: IDENTITY_FIELD_LABEL_ID[field],
                                        })
                                    ),
                                    { type: 'conjunction' }
                                ),
                            }
                        )}
                    </Box>
                )}
            </Box>

            {verdict && (
                <VerdictBadge verdict={verdict} onRetryCheck={onRetryCheck} />
            )}
        </Box>
    );
}

function VerdictBadge({
    verdict,
    onRetryCheck,
}: {
    verdict: Verdict;
    onRetryCheck?: () => void;
}) {
    const intl = useIntl();
    if (verdict.kind === 'checking') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontFamily: 'fontFamilyMonospace',
                    fontSize: 11,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                <CircularProgress size={12} />
                {intl.formatMessage({
                    id: 'patientList.import.identity.checking',
                })}
            </Box>
        );
    }

    if (verdict.kind === 'failed') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: 12,
                    color: 'error.main',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                <AlertTriangle size={13} />
                {intl.formatMessage({
                    id: 'patientList.import.identity.checkFailed',
                })}
                {onRetryCheck && (
                    <QuietButton onClick={onRetryCheck}>
                        {intl.formatMessage({
                            id: 'patientList.import.identity.retryCheck',
                        })}
                    </QuietButton>
                )}
            </Box>
        );
    }

    const isExisting = verdict.target.kind === 'existing-patient';
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                flexShrink: 0,
                height: 22,
                px: 1,
                fontSize: 11,
                fontWeight: 500,
                borderRadius: 1,
                border: 1,
                whiteSpace: 'nowrap',
                color: isExisting ? 'primary.main' : 'success.main',
                borderColor: isExisting ? 'primary.main' : 'success.main',
                bgcolor: (theme) =>
                    isExisting
                        ? theme.palette.action.selected
                        : alpha(theme.palette.success.main, 0.12),
            }}
        >
            {isExisting ? (
                <>
                    <UserCheck size={12} />{' '}
                    {intl.formatMessage({
                        id: 'patientList.import.identity.existingPatient',
                    })}
                </>
            ) : (
                <>
                    <UserPlus size={12} />{' '}
                    {intl.formatMessage({
                        id: 'patientList.import.identity.newPatient',
                    })}
                </>
            )}
        </Box>
    );
}
